/**
 * BrowseSearchBar — founder direction 9 Feb 2026:
 *   "yes i agree remove the chips and let them e part of the search bar"
 *
 * One unified shell that holds the smart search input on top and the
 * live-count filter chips inline below. Visually it reads as ONE
 * search bar with chips attached · functionally the input still
 * routes through the existing LLM query parser (Best bars in Dubai →
 * tab=bars&city=Dubai) and the chips still own the URL filter state.
 *
 * Used only on the browse view (tab-grid surface). The home + results
 * views keep the original SearchField on its own — they don't carry
 * a filter strip.
 */
import React, { useState } from "react";
import { BrowseFilterStrip } from "./BrowseFilterStrip";


export function BrowseSearchBar({
    tab,
    filters,
    onChange,
    onClearAll,
    onRunSearch,
    initialQuery = "",
    filteredTotal = null,
}) {
    const [q, setQ] = useState(initialQuery);
    const [focus, setFocus] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        const text = (q || "").trim();
        if (!text) return;
        onRunSearch?.(text);
    };

    return (
        <div
            data-testid="browse-search-bar"
            style={{
                maxWidth: 1152,
                marginInline: "auto",
                paddingInline: 24,
                paddingTop: 16,
            }}
        >
            <div
                style={{
                    background: "var(--t-card)",
                    border: `1.5px solid ${focus ? "var(--t-brass)" : "var(--t-line2)"}`,
                    borderRadius: 18,
                    overflow: "hidden",
                    transition: "border-color 120ms ease",
                }}
            >
                {/* Smart search input · routes natural language */}
                <form
                    onSubmit={handleSubmit}
                    className="flex items-center gap-2"
                    style={{ padding: "10px 14px" }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            color: focus ? "var(--t-brass)" : "var(--t-ink3)",
                            fontSize: 18,
                        }}
                    >
                        ⌕
                    </span>
                    <input
                        data-testid="browse-search-input"
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onFocus={() => setFocus(true)}
                        onBlur={() => setFocus(false)}
                        placeholder="Search a venue or city — try Gymkhana London, Best bars Dubai, near me"
                        autoComplete="off"
                        aria-label="Search a venue or city — type to escape this filter"
                        className="flex-1 min-w-0 bg-transparent outline-none px-2"
                        style={{
                            fontSize: 15,
                            color: "var(--t-ink)",
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            border: 0,
                        }}
                    />
                    {/* Always-visible Search affordance · founder catch
                        10 Feb 2026 (BC-006): in the filtered Bars-London
                        view, a member must NEVER feel STUCK · the
                        Search button below the cursor proves the input
                        is typeable AND clickable to fire the search.
                        Earlier behaviour (button only when q.trim()
                        non-empty) hid the affordance until after the
                        member already realised they could type. */}
                    <button
                        type="submit"
                        data-testid="browse-search-submit"
                        disabled={!q.trim()}
                        aria-label={q.trim() ? "Run search" : "Type to search"}
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "6px 14px",
                            borderRadius: 999,
                            background: q.trim() ? "var(--t-ink)" : "transparent",
                            color: q.trim() ? "var(--t-paper)" : "var(--t-ink3)",
                            border: q.trim() ? "0" : "1px solid var(--t-line2)",
                            cursor: q.trim() ? "pointer" : "default",
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            letterSpacing: "0.02em",
                            whiteSpace: "nowrap",
                            transition: "background-color 120ms ease, color 120ms ease",
                        }}
                    >
                        Search
                    </button>
                </form>

                {/* Filter chips · attached row inside the same shell */}
                <div
                    data-testid="browse-search-bar-chips"
                    style={{
                        borderTop: "1px solid var(--t-line2, rgba(0,0,0,0.06))",
                    }}
                >
                    <BrowseFilterStrip
                        tab={tab}
                        filters={filters}
                        onChange={onChange}
                        onClearAll={onClearAll}
                        filteredTotal={filteredTotal}
                        embedded={true}
                    />
                </div>
            </div>
        </div>
    );
}
